// API client for frontend

const API_BASE = '/api';

interface ApiError {
  error: string;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Include cookies for session
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as ApiError).error || 'Request failed');
  }

  return data as T;
}

// Auth
export async function checkAuth(): Promise<{ authenticated: boolean }> {
  return request('/auth/check');
}

export async function login(password: string): Promise<{ success: boolean }> {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function logout(): Promise<{ success: boolean }> {
  return request('/auth/logout', { method: 'POST' });
}

// Settings
export interface Settings {
  seatsAeroApiKey: string;
  pollIntervalMinutes: number;
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    pass: string;
    from: string;
  };
  accessPassword: string;
}

export async function getSettings(): Promise<Settings> {
  return request('/settings');
}

export async function updateSettings(settings: Partial<Settings>): Promise<Settings> {
  return request('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// Watchers
export interface NotificationConfig {
  type: 'email' | 'webhook';
  emails?: string[];
  url?: string;
  headers?: Record<string, string>;
}

export interface Watcher {
  id: string;
  name: string;
  enabled: boolean;
  origins: string[];
  destinations: string[];
  programs: string[];
  excludeAirlines: string[];
  cabinClasses: ('economy' | 'premium_economy' | 'business' | 'first')[];
  dateStart: string;
  dateEnd: string;
  directOnly: boolean;
  maxPoints: Record<string, number>;
  maxPriceUsd: Record<string, number>;
  minSeats: number;
  centsPerPoint: Record<string, number>;
  notifications: NotificationConfig[];
  createdAt: string;
  updatedAt: string;
}

export async function getWatchers(): Promise<Watcher[]> {
  return request('/watchers');
}

export async function getWatcher(id: string): Promise<Watcher> {
  return request(`/watchers/${id}`);
}

export async function createWatcher(
  watcher: Omit<Watcher, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Watcher> {
  return request('/watchers', {
    method: 'POST',
    body: JSON.stringify(watcher),
  });
}

export async function updateWatcher(
  id: string,
  updates: Partial<Omit<Watcher, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<Watcher> {
  return request(`/watchers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteWatcher(id: string): Promise<{ success: boolean }> {
  return request(`/watchers/${id}`, { method: 'DELETE' });
}

export async function toggleWatcher(id: string): Promise<Watcher> {
  return request(`/watchers/${id}/toggle`, { method: 'POST' });
}

export async function duplicateWatcher(id: string): Promise<Watcher> {
  return request(`/watchers/${id}/duplicate`, { method: 'POST' });
}

// Notifications
export interface NotificationLog {
  id: string;
  watcherId: string;
  flightKey: string;
  lastNotifiedPoints: number;
  lastNotifiedPriceUsd: number;
  lastNotifiedAt: string;
  flightDetails: string;
}

export interface PaginatedNotifications {
  items: NotificationLog[];
  total: number;
  limit: number;
  offset: number;
}

export async function getNotifications(
  watcherId?: string,
  limit = 20,
  offset = 0
): Promise<PaginatedNotifications> {
  const params = new URLSearchParams();
  if (watcherId) params.set('watcherId', watcherId);
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  return request(`/notifications?${params}`);
}

export async function deleteNotification(id: string): Promise<{ success: boolean }> {
  return request(`/notifications/${id}`, { method: 'DELETE' });
}

export async function clearNotifications(watcherId?: string): Promise<{ success: boolean; deleted: number }> {
  const params = new URLSearchParams();
  if (watcherId) params.set('watcherId', watcherId);
  return request(`/notifications?${params}`, { method: 'DELETE' });
}

// Polling
export interface PollingStatus {
  isRunning: boolean;
  isPolling: boolean;
  lastPollTime: string | null;
  lastResults: { watcherId: string; matchCount: number; notifiedCount: number }[];
  intervalMinutes: number;
}

export async function getPollingStatus(): Promise<PollingStatus> {
  return request('/polling/status');
}

export async function triggerPoll(): Promise<{ success: boolean; message: string }> {
  return request('/polling/trigger', { method: 'POST' });
}

// Presets
export interface MileageProgram {
  code: string;
  name: string;
  alliance: string;
  defaultCpp: number;
}

export interface AirportPresetsResponse {
  presets: Record<string, string[]>;
  groups: Record<string, Record<string, string[]>>;
}

export async function getAirportPresets(): Promise<AirportPresetsResponse> {
  return request('/presets/airports');
}

export async function getProgramPresets(): Promise<{
  programs: MileageProgram[];
  defaultCpp: Record<string, number>;
}> {
  return request('/presets/programs');
}

// Exchange Rates
export interface ExchangeRatesInfo {
  date: string;
  fetchedAt: number;
  rates: Record<string, number>; // currency -> USD rate
}

export async function getExchangeRates(): Promise<ExchangeRatesInfo | null> {
  return request('/exchange-rates');
}

export async function refreshExchangeRates(): Promise<ExchangeRatesInfo | null> {
  return request('/exchange-rates', { method: 'POST' });
}
